import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import {
  BulkChangeAssetOwnershipDocument,
  type BulkChangeAssetOwnershipMutation,
  type BulkChangeAssetOwnershipMutationVariables,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  AssignmentForm,
  type AssignmentFormData,
} from 'modules/it_management/components/AssignmentForm'
import { useAssignmentFormSaveDisabled } from 'modules/it_management/hooks/useAssignmentFormSaveDisabled'

type Asset = {
  id: string
  itAssetModel?: {
    brand?: string | null
    name?: string | null
  } | null
  serialNumber?: string | null
}

interface Props {
  ids: string[]
  assets: Asset[]
  onClose: () => void
}

export const BulkChangeAssetOwnershipModal = ({ ids, assets, onClose }: Props) => {
  const [bulkChangeAssetOwnership, { loading: assigning }] = useMutation<
    BulkChangeAssetOwnershipMutation,
    BulkChangeAssetOwnershipMutationVariables
  >(BulkChangeAssetOwnershipDocument)

  const [formData, setFormData] = useState<AssignmentFormData>({
    assignmentType: 'none',
    selectedEmployeeId: undefined,
    selectedWorkplace: null,
    selectedTeam: null,
    selectedLocation: null,
    deviceStatus: 'in_stock',
  })

  const isSaveDisabled = useAssignmentFormSaveDisabled(formData.assignmentType)

  const handleBulkChangeOwnership = async () => {
    try {
      let ownerId: number | null = null
      let locationId: number | null = null
      let teamId: number | null = null
      let workplaceId: number | null = null

      switch (formData.assignmentType) {
        case 'person':
          ownerId = formData.selectedEmployeeId ?? null
          break
        case 'workplace':
          workplaceId = formData.selectedWorkplace ? Number(formData.selectedWorkplace.id) : null
          break
        case 'team':
          teamId = formData.selectedTeam ? Number(formData.selectedTeam.id) : null
          break
        case 'space':
          locationId = formData.selectedLocation ? Number(formData.selectedLocation.id) : null
          break
        case 'none':
          ownerId = null
          locationId = null
          teamId = null
          workplaceId = null
          break
      }

      await bulkChangeAssetOwnership({
        variables: {
          ids,
          ownerId,
          locationId,
          teamId,
          workplaceId,
          status: formData.assignmentType === 'none' ? formData.deviceStatus : undefined,
        },
        refetchQueries: ['ItAssetsTable', 'ItAssetDetails', 'ItAssetsStatusCounts'],
        awaitRefetchQueries: true,
      })

      ids.forEach((_) => {
        track(trackingEvents.itManagement.assigmentEquipment.submit, {
          origin: 'table_view',
          assign_to: formData.assignmentType,
        })
      })

      app.setFlash({
        type: 'correct',
        message:
          formData.assignmentType !== 'none'
            ? i18n.t('it_management.assets.bulk_assign.success_message')
            : i18n.t('it_management.assets.bulk_assign.unassigned_message'),
      })
      onClose()
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.assets.bulk_assign.error_message'),
      })
    }
  }

  const getAssetName = (asset: Asset) => {
    if (asset.itAssetModel?.brand && asset.itAssetModel?.name) {
      return `${asset.itAssetModel.brand} ${asset.itAssetModel.name}`
    }
    if (asset.serialNumber) {
      return asset.serialNumber
    }
    return i18n.t('it_management.assets.assign_owner_modal.unknown_asset')
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('it_management.assets.assign_owner_modal.title')}
      primaryAction={{
        label: i18n.t('it_management.assets.bulk_assign.assign'),
        onClick: handleBulkChangeOwnership,
        loading: assigning,
        disabled: assigning || isSaveDisabled,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
        disabled: assigning,
      }}
    >
      <Box gap="s16">
        <Box background="grey100" padding="s16" borderRadius={{ all: 'abs004' }} gap="s8">
          <Text size="200" color="grey800" weight="semibold">
            {i18n.t('it_management.assets.presets.asset')}:
          </Text>
          {assets.map((asset) => (
            <Text key={asset.id} size="200" color="grey800">
              • {getAssetName(asset)}
            </Text>
          ))}
        </Box>
        <AssignmentForm value={formData} onChange={setFormData} />
      </Box>
    </F0Dialog>
  )
}
